import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { MainActions } from '../../actions/MainActions';
import { SessionHelper } from '../../helpers/SessionHelper';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { Body } from '../../common-components/Layout/Body/Body';
import { Header } from '../../common-components/Layout/Header/Header';
import { Page } from '../../common-components/Layout/Page/Page';
import { IBreadcrumbItem } from '../../common-components/Breadcrumb/Breadcrumb';
import { ActionsPanel } from '../../components/ActionsPanel/ActionsPanel';
import { DnpLink } from '../../models/DnpLink';
import { PaginatedDnpLinksList } from './PaginatedDnpLinksList/PaginatedDnpLinksList';
import { Container } from './styles';
import { ActionsForDnpLinks } from './ActionsForDnpLinks/ActionsForDnpLinks';
import { ModalNewDnpLink } from './ModalNewDnpLink/ModalNewDnpLink';

const ManageDnpLinks: React.FC = () => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [version, setVersion] = React.useState<number>(1);
  const [dnpLinks, setDnpLinks] = React.useState<DnpLink[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(0);
  const [amountOfPages, setAmountOfPages] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [filtering, setFiltering] = React.useState<boolean>(true);
  const [modalNewDnpLinkVisible, setModalNewDnpLinkVisible] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const { session, signOut } = useAuth();

  const {
    getListOfValidDnpLinkService,
    toastService
  } = React.useContext(ServicesContext);

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => {
    const memo: IBreadcrumbItem[] = [
      {
        text: strings.Home,
        route: routes.Home
      }
    ];
    return memo;
  }, []);

  useEffect(() => {
    if (!session || SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    filter();
  }, [session, navigate]);

  const memoActionsForDnpLinks = React.useMemo(() => {
    return (
      <ActionsForDnpLinks
        filtering={filtering}
        updateList={() => updateList()}
        getNewLink={() => setModalNewDnpLinkVisible(true)}
      />
    );

  }, [filtering, dnpLinks, modalNewDnpLinkVisible]);

  const headerRender = React.useMemo(() => {
    return (
      <Header
        title={strings.ManageDnpLinks}
        breadcrumbItems={breadcrumbItems}
        version={version}
        alertSummary={false}
      />
    );
  }, [version]);

  var memoModalNewDnpLink = React.useMemo(() => {
    if (!modalNewDnpLinkVisible) return null;

    return (
      <ModalNewDnpLink
        close={() => closeModalNewDnpLink()}
      />
    );
  }, [modalNewDnpLinkVisible, filtering, dnpLinks]);

  const bodyRender = React.useMemo(() => {

    return (
      <Container>
        {memoModalNewDnpLink}
        <ActionsPanel>
          {memoActionsForDnpLinks}
        </ActionsPanel>
        <PaginatedDnpLinksList
          currentPage={currentPage}
          currentPageSize={currentPageSize}
          amountOfPages={amountOfPages}
          totalAmount={totalAmount}
          dnpLinks={dnpLinks}
          filterAreaHeight={0}
          pageSize={pageSize}
          changePageSize={updateList}
          changePage={updateList}
          filtering={filtering}
        />
      </Container>
    );
  }, [dnpLinks, currentPage, currentPageSize, amountOfPages, pageSize, totalAmount, filtering, modalNewDnpLinkVisible]);

  return (
    <Page header={headerRender}>
      <Body>
        {bodyRender}
      </Body>
    </Page>
  );

  function updateList() {
    if (SessionHelper.isExpired(session)) {
      navigate(routes.Login);
      return;
    }
    filter();
  }

  function filter() {
    mainActions.setNotWait(true);
    setFiltering(true);
    getListOfValidDnpLinkService.handle()
      .then(result => {
        setDnpLinks(result?.value?.list || []);
        setCurrentPage(1);
        setCurrentPageSize(0);
        setAmountOfPages(result?.value?.list?.length ? 1 : 0);
        setTotalAmount(result?.value?.list?.length || 0);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setFiltering(false);
      });
  }

  function closeModalNewDnpLink() {
    setModalNewDnpLinkVisible(false);
    updateList();
  }
}

export default ManageDnpLinks;